import React, { useContext, useState, useEffect, useRef } from 'react';
import ReactSVG from 'react-svg';
import times from 'times.svg';
import minus from 'minus.svg';
import plus from 'plus.svg';
import cash from 'cash.svg';
import useWindowSize from '../../../hooks/useWindowSize.js';
import filter_types from '../../../constants/filter_types.json';
import filter_amenities from '../../../constants/filter_amenities.json';
import { ListingMapContext } from '../../../context/ListingMapContext.js';
import WindowQuery from '../../../utils/WindowQuery.js';

export default function FiltersDropdown() {
  const updateQuery = new WindowQuery();
  const { width: windowWidth } = useWindowSize();

  const isMobile = windowWidth <= 768;

  const {
    showFilters,
    toggleFilters,
    propertyType,
    bedrooms,
    bathrooms,
    rentalSearchAmenities,
    minPrice,
    maxPrice,
    setMinPrice,
    setMaxPrice,
    setPropertyType,
    setBedrooms,
    setBathrooms,
    clearAllFilters,
    communityId,
    setCommunityId,
    setSearchOnMove,
    city,
    cityId,
    setCity,
    setCityId,
    setRentalSearchAmenities,
  } = useContext(ListingMapContext);

  const topOfDropdown = useRef();

  const MIN_BEDROOMS = 0;
  const MAX_BEDROOMS = 10;
  const MIN_BATHROOMS = 0;
  const MAX_BATHROOMS = 5;

  const [localTypes, setLocalTypes] = useState('');
  const [localBedrooms, setLocalBedrooms] = useState(MIN_BEDROOMS);
  const [localBathrooms, setLocalBathrooms] = useState(MIN_BATHROOMS);
  const [localAmenities, setLocalAmenities] = useState([]);
  const [localMinPrice, setLocalMinPrice] = useState();
  const [localMaxPrice, setLocalMaxPrice] = useState();
  const [minMaxError, setMinMaxError] = useState('');

  useEffect(() => {
    if (propertyType || propertyType === '') setLocalTypes(propertyType);
    if (bedrooms) setLocalBedrooms(parseInt(bedrooms));
    if (bathrooms) setLocalBathrooms(parseInt(bathrooms));
    if (rentalSearchAmenities.length)
      setLocalAmenities(
        rentalSearchAmenities.map(a => decodeURI(a).toLowerCase())
      );
    setLocalMinPrice(parseInt(minPrice));
    setLocalMaxPrice(parseInt(maxPrice));
  }, [
    rentalSearchAmenities,
    bathrooms,
    bedrooms,
    maxPrice,
    minPrice,
    propertyType,
  ]);

  const handleApply = () => {
    setBedrooms(localBedrooms);
    setBathrooms(localBathrooms);
    setPropertyType(localTypes);
    if (localMinPrice) setMinPrice(localMinPrice);
    if (localMaxPrice) setMaxPrice(localMaxPrice);
    toggleFilters(false);

    setRentalSearchAmenities(localAmenities);

    // Update Bedrooms Query
    if (localBedrooms) updateQuery.add('beds', parseInt(localBedrooms));
    else updateQuery.remove('beds');

    // Update Bathrooms Query
    if (localBathrooms) updateQuery.add('baths', parseInt(localBathrooms));
    else updateQuery.remove('baths');

    // Update Types Query
    if (localTypes.length) {
      updateQuery.add('property_type', localTypes);
    } else {
      updateQuery.remove('property_type');
    }

    // Update Amenities Query
    if (localAmenities.length) {
      updateQuery.add(
        'amenities',
        localAmenities.map(t => t.toLowerCase()).join(',')
      );
    } else {
      updateQuery.remove('amenities');
    }

    // Update Min Price Query
    if (localMinPrice) updateQuery.add('min_price', parseInt(localMinPrice));
    else updateQuery.remove('min_price');

    // Update Max Price Query
    if (localMaxPrice) updateQuery.add('max_price', parseInt(localMaxPrice));
    else updateQuery.remove('max_price');
  };

  const handleClear = () => {
    // Remove queries
    updateQuery.remove('beds');
    updateQuery.remove('baths');
    updateQuery.remove('property_type');
    updateQuery.remove('amenities');
    if (isMobile) {
      updateQuery.remove('min_price');
      updateQuery.remove('max_price');
    }
    // Reset Local State
    setLocalTypes('');
    setLocalBedrooms(MIN_BEDROOMS);
    setLocalBathrooms(MIN_BATHROOMS);
    setLocalAmenities([]);
    setLocalMinPrice('');
    setLocalMaxPrice('');
    // Reset Context State
    setPropertyType('');
    setBedrooms(MIN_BEDROOMS);
    setBathrooms(MIN_BATHROOMS);
    setRentalSearchAmenities([]);
    if (isMobile) {
      setMinPrice('');
      setMaxPrice('');
    }
  };

  const filterAmenityHash = {
    private_pool: 'Private Pool',
    comm_pool: 'Community Pool',
    gulf_view: 'Gulf View',
    gulf_front: 'Gulf Front',
    pets: 'Pets',
    golf_cart: 'Golf Cart',
    handicap_accessible: 'Handicap Accessible',
    condo: 'Condo',
    home: 'House',
    townhome: 'Townhome',
  };

  const scrollToRef = ref => {
    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <div ref={topOfDropdown} className="filters-dropdown" hidden={!showFilters}>
      <section className={windowWidth <= 768 ? 'mobile-filter-buttons' : null}>
        <button
          aria-label="Close Filter Dropdown"
          name="Close Filter Dropdown"
          type="button"
          className="close-filter-dropdown"
          onClick={() => toggleFilters(false)}
        >
          <ReactSVG src={times} />
        </button>
      </section>
      <section className="filter-dropdown-actions">
        {isMobile && (
          <button
            type="button"
            name="Clear All Filters"
            title="Clear All Filters"
            aria-label="Clear All Filters"
            className="btn btn--small btn--light"
            onClick={() => {
              clearAllFilters('rentals');
            }}
          >
            Clear All Filters
          </button>
        )}
        {communityId && isMobile && (
          <button
            type="button"
            name="Remove Community Filter"
            aria-label="Remove Community Filter"
            title="Remove Community Filter"
            className="btn btn--small btn--light"
            onClick={() => {
              updateQuery.remove('comm_id');
              setCommunityId(null);
              setSearchOnMove(false);
            }}
          >
            Remove Community
          </button>
        )}
        {(city || cityId) && isMobile && (
          <button
            type="button"
            name="Remove Community Filter"
            aria-label="Remove Community Filter"
            title="Remove Community Filter"
            className="btn btn--small btn--light"
            onClick={() => {
              updateQuery.remove('area');
              setCity(null);
              setCityId(null);
            }}
          >
            Remove City
          </button>
        )}
      </section>
      {isMobile && (
        <section
          className="filter-dropdown-section price-input"
          style={{ marginBottom: '28px' }}
        >
          <h4 className="filter-dropdown-section-title">Price</h4>

          <div
            className="filter-options clear"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <section className="dropdown-filter-row">
              <label htmlFor="min_price_map_filter">
                <h2 className="dropdown-title">Min Price</h2>
                <div className="dropdown-input-container">
                  <ReactSVG src={cash} />
                  <input
                    aria-label="Minimum Price"
                    id="min_price_map_filter"
                    className="dropdown-filter-input"
                    placeholder="0"
                    type="number"
                    value={localMinPrice}
                    onChange={e => setLocalMinPrice(e.target.value)}
                    style={{ border: minMaxError ? '1px solid #b94a48' : null }}
                  />
                </div>
              </label>

              <label
                htmlFor="max_price_map_filter"
                style={{ marginLeft: '15px' }}
              >
                <h2 className="dropdown-title">Max Price</h2>
                <div className="dropdown-input-container">
                  <ReactSVG src={cash} />
                  <input
                    aria-label="Maximum Price"
                    id="max_price_map_filter"
                    className="dropdown-filter-input"
                    placeholder="None"
                    type="number"
                    value={localMaxPrice}
                    onChange={e => setLocalMaxPrice(e.target.value)}
                    style={{ border: minMaxError ? '1px solid #b94a48' : null }}
                    onFocus={() => setMinMaxError('')}
                  />
                </div>
              </label>
            </section>
            {minMaxError && (
              <div style={{ marginTop: '6px', color: '#b94a48' }}>
                {minMaxError}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="filter-dropdown-section checkbox">
        <h4 className="filter-dropdown-section-title">Type</h4>

        <div className="filter-options">
          {filter_types.map((type, ind) => (
            <div key={ind}>
              <label key={type} htmlFor={`filters-type-${type}`}>
                <input
                  aria-label={`${type}`}
                  id={`filters-type-${type}`}
                  type="checkbox"
                  onChange={() => {
                    const typeName = filterAmenityHash[type].toLowerCase();
                    if (localTypes === typeName) {
                      setLocalTypes('');
                    } else {
                      setLocalTypes(typeName);
                    }
                  }}
                  checked={localTypes === filterAmenityHash[type].toLowerCase()}
                />
                <p>{filterAmenityHash[type]}</p>
              </label>
            </div>
          ))}
        </div>
      </section>

      <section className="filter-dropdown-section increment">
        <div>
          <h4 className="filter-dropdown-section-title">Bedrooms</h4>
          <section className="increment-inputs">
            <button
              aria-label="Subtract Bedrooms"
              name="Subtract Bedrooms"
              type="button"
              className={`btn btn--round subtract ${
                localBedrooms === MIN_BEDROOMS ? 'disabled' : ''
              }`}
              onClick={() =>
                setLocalBedrooms(Math.max(localBedrooms - 1, MIN_BEDROOMS))
              }
            >
              <ReactSVG src={minus} />
            </button>
            <h3>{localBedrooms}+</h3>
            <button
              aria-label="Add Bedrooms"
              name="Add Bedrooms"
              type="button"
              className={`btn btn--round add ${
                localBedrooms === MAX_BEDROOMS ? 'disabled' : ''
              }`}
              onClick={() =>
                setLocalBedrooms(Math.min(localBedrooms + 1, MAX_BEDROOMS))
              }
            >
              <ReactSVG src={plus} />
            </button>
          </section>
        </div>

        <div>
          <h4 className="filter-dropdown-section-title">Bathrooms</h4>
          <section className="increment-inputs">
            <button
              aria-label="Subtract bathrooms"
              name="Subtract bathrooms"
              type="button"
              className={`btn btn--round subtract ${
                localBathrooms === MIN_BATHROOMS ? 'disabled' : ''
              }`}
              onClick={() =>
                setLocalBathrooms(Math.max(localBathrooms - 1, MIN_BATHROOMS))
              }
            >
              <ReactSVG src={minus} />
            </button>
            <h3>{localBathrooms}+</h3>
            <button
              aria-label="Add Bathrooms"
              name="Add Bathrooms"
              type="button"
              className={`btn btn--round add ${
                localBathrooms === MAX_BATHROOMS ? 'disabled' : ''
              }`}
              onClick={() =>
                setLocalBathrooms(Math.min(localBathrooms + 1, MAX_BATHROOMS))
              }
            >
              <ReactSVG src={plus} />
            </button>
          </section>
        </div>
      </section>

      <section className="filter-dropdown-section checkbox amenities">
        <h4 className="filter-dropdown-section-title">Amenities</h4>

        <div className="filter-options">
          {filter_amenities.map((amenity, ind) => (
            <div key={ind}>
              <label key={amenity} htmlFor={`filters-amenity-${amenity}`}>
                <input
                  aria-label={`${amenity}`}
                  id={`filters-amenity-${amenity}`}
                  type="checkbox"
                  onChange={() => {
                    if (localAmenities.includes(amenity.toLowerCase())) {
                      // Create a copy of amenities
                      const amenitiesCopy = [...localAmenities];
                      // Find the index of the amenity
                      const index = amenitiesCopy.findIndex(
                        cur => cur === amenity.toLowerCase()
                      );
                      // Remove the amenity
                      amenitiesCopy.splice(index, 1);
                      // Set amenities
                      setLocalAmenities(amenitiesCopy);
                    } else {
                      setLocalAmenities([
                        ...localAmenities,
                        amenity.toLowerCase(),
                      ]);
                    }
                  }}
                  checked={localAmenities.includes(amenity.toLowerCase())}
                />
                <p>{filterAmenityHash[amenity]}</p>
              </label>
            </div>
          ))}
        </div>
      </section>

      <section className="dropdown-apply">
        <button
          aria-label="Apply Filters"
          name="Apply Filters"
          type="button"
          className="btn btn--dark Pbtn--small apply"
          onClick={() => {
            if (
              localMaxPrice &&
              parseInt(localMinPrice) > parseInt(localMaxPrice)
            ) {
              setMinMaxError('Max must be greater than min');
              scrollToRef(topOfDropdown);
            } else {
              handleApply();
            }
          }}
        >
          Apply
        </button>
        <button
          aria-label="Clear Filters"
          name="Clear Filters"
          type="button"
          className="btn btn--small clear-filter"
          onClick={handleClear}
        >
          Clear
        </button>
      </section>
    </div>
  );
}
